import React from 'react';

import { Box, Center, HStack, Stack } from '@chakra-ui/react';
import { customTheme } from 'theme';

import { AreaChart, LineChart } from 'components/Visualization';
import { DashboardWidget } from 'components/Visualization/CDashboard/Widget/DashboardWidget';
import {
  customizedXAxisTick,
  customizedYAxisTick,
} from 'components/Visualization/Charts/utils';
import { LegendProps } from 'containers/Dashboard/types';
import { DashboardOverviewCardProps } from 'containers/Dashboard/utils/DashboardOverviewCard';
import {
  renderChange,
  renderLegend,
  renderLegendsAtTop,
} from 'containers/Dashboard/utils/utils';

export interface CardWithAreaChartProps
  extends Omit<DashboardOverviewCardProps, 'content'> {
  legends?: LegendProps[];
  chartData: Record<string, any>[];
  isLoadingChart?: boolean;
  yFields: string[];
  colorMap: Record<string, string>;
  placeholder?: string;
  change?: number;
  legendW?: number;
  showLegendAtTop?: boolean;
  legendsSpacing?: number;
}

export const CardWithAreaChart: React.FC<CardWithAreaChartProps> = props => {
  const {
    legends,
    chartData,
    isLoadingChart,
    yFields,
    colorMap,
    placeholder,
    change = 0,
    legendW = legends ? 30 : 0,
    showLegendAtTop = false,
    legendsSpacing,
    ...rest
  } = props;

  return (
    <DashboardWidget
      subHeader="Last 30 Days - Daily"
      content={
        placeholder && !chartData?.length ? (
          <Center color={customTheme.colors.gray['250']}>{placeholder}</Center>
        ) : (
          <Stack
            w="full"
            spacing={0}
            h="full"
            direction={showLegendAtTop ? 'column' : 'row'}
          >
            {legends && !showLegendAtTop && (
              <Box w={legendW + '%'} h="full" px={3}>
                {renderLegend(legends, 0)}
              </Box>
            )}
            {legends && showLegendAtTop && (
              <HStack justify="space-between" px={1}>
                <Box>{renderLegendsAtTop(legends, legendsSpacing)}</Box>
                <Box>{renderChange(change)}</Box>
              </HStack>
            )}
            <Stack w={100 - (showLegendAtTop ? 0 : legendW) + '%'} h="full">
              {!showLegendAtTop && renderChange(change)}
              <Box w="full" h="full" py={0}>
                <AreaChart
                  data={chartData}
                  xField="Date"
                  yField={yFields}
                  isLoading={isLoadingChart}
                  legend={false}
                  colorMap={colorMap}
                  styles={{
                    yAxis: {
                      axisLine: false,
                      tickLine: false,
                      tick: props =>
                        customizedYAxisTick({
                          ...props,
                          style: { fontSize: 14, x: -6 },
                        }),
                    },
                    xAxis: {
                      interval: 'preserveStartEnd',
                      ticks: [
                        chartData?.[0]?.Date,
                        chartData?.[chartData?.length - 1]?.Date,
                      ],
                      tick: props =>
                        customizedXAxisTick({
                          ...props,
                          style: { fontSize: 14, fontWeight: 600, y: 6 },
                        }),
                    },
                  }}
                />
              </Box>
            </Stack>
          </Stack>
        )
      }
      {...rest}
    />
  );
};
